import { useMedplum } from '@medplum/react-hooks';
import { triggerPasswordResetEmail } from '../../lib/securitySettingsService';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Center,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconArrowLeft, IconCheck, IconMail } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ForgotPasswordFormValues {
  email: string;
}

/**
 * Forgot Password page for password reset requests
 *
 * Flow:
 * 1. User enters their email address
 * 2. App calls Medplum's reset password endpoint
 * 3. User receives email with password reset link
 * 4. User clicks link to complete password reset
 */
export function ForgotPasswordPage(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const form = useForm<ForgotPasswordFormValues>({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email address';
        return null;
      },
    },
  });

  const handleSubmit = useCallback(
    async (values: ForgotPasswordFormValues) => {
      setLoading(true);
      setError(null);

      try {
        const email = values.email.trim().toLowerCase();
        await triggerPasswordResetEmail(medplum, email);
        setSuccess(true);
      } catch (err) {
        console.error('Password reset error:', err);
        setError(err instanceof Error ? err.message : 'Could not send password reset email.');
      } finally {
        setLoading(false);
      }
    },
    [medplum]
  );

  const handleBackToSignIn = (): void => {
    navigate('/signin');
  };

  return (
    <Box
      bg="unifyGold.0"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        minHeight: '100dvh',
        boxSizing: 'border-box',
      }}
    >
      <Center h="100%" p="md">
        <Paper shadow="md" p="lg" radius="md" w={400} maw="calc(100vw - 2rem)" bg="white">
          <Stack gap="md">
            <Box ta="center">
              <Title order={2} c="unifyGold.6">
                Reset Password
              </Title>
              <Text c="dimmed" size="sm" mt="xs">
                {success
                  ? 'Check your email for reset instructions'
                  : "Enter your email and we'll send you a reset link"}
              </Text>
            </Box>

            {error && (
              <Alert color="red" title="Error">
                {error}
              </Alert>
            )}

            {success ? (
              <Stack gap="md">
                <Alert icon={<IconCheck size={16} />} color="green" title="Email Sent">
                  If an account exists with that email, you will receive password reset instructions shortly.
                  Please check your inbox and spam folder. Reset links expire 15 minutes after the email is sent.
                </Alert>

                <Button
                  fullWidth
                  size="md"
                  variant="light"
                  leftSection={<IconArrowLeft size={20} />}
                  onClick={handleBackToSignIn}
                >
                  Back to Sign In
                </Button>
              </Stack>
            ) : (
              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                  <TextInput
                    label="Email Address"
                    placeholder="you@example.com"
                    autoComplete="email"
                    leftSection={<IconMail size={16} />}
                    {...form.getInputProps('email')}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    size="md"
                    loading={loading}
                  >
                    Send Reset Link
                  </Button>

                  <Center>
                    <Anchor component="button" type="button" c="dimmed" size="sm" onClick={handleBackToSignIn}>
                      <Group gap={4}>
                        <IconArrowLeft size={14} />
                        Back to Sign In
                      </Group>
                    </Anchor>
                  </Center>
                </Stack>
              </form>
            )}

            <Text size="xs" c="dimmed" ta="center">
              For security reasons, we show the same message whether or not an account exists.
            </Text>
          </Stack>
        </Paper>
      </Center>
    </Box>
  );
}
